import React, { useEffect, useState } from 'react';
import { Play, Pause, Volume2, Timer, Save, Square, Clock, Music, X, Target } from 'lucide-react';
import { useAppStore } from '../store/useAppStore';
import { getFrequencyById } from '../data/frequencies';
import { getBackgroundSoundById } from '../data/backgroundSounds';
import AdvancedFrequencyControls from './AdvancedFrequencyControls';
import HeadphoneQualitySelector from './HeadphoneQualitySelector';
import './PlaybackBar.css';

const PlaybackBar: React.FC = () => {
  const {
    currentFrequencies,
    currentBackgroundSounds,
    isPlaying,
    masterVolume,
    playbackTimerRemaining,
    isTimerActive,
    setPlaying,
    setMasterVolume,
    stopAll,
    setPlaybackTimerRemaining,
    setIsTimerActive,
    setPlaybackTimer,
    saveToPlaylist,
    addFrequency,
    setBackgroundSoundVolume,
    isFocusMode,
    setFocusMode
  } = useAppStore();

  const [showTimerModal, setShowTimerModal] = useState(false);
  const [timerMinutes, setTimerMinutes] = useState(30);

  useEffect(() => {
    if (isTimerActive && playbackTimerRemaining !== null && playbackTimerRemaining > 0) {
      const interval = setInterval(() => {
        setPlaybackTimerRemaining((prev) => {
          if (prev === null || prev <= 0) {
            setIsTimerActive(false);
            // Stop all asynchronously when timer reaches 0
            stopAll().catch(console.error);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    } else if (isTimerActive && playbackTimerRemaining === 0) {
      (async () => {
        await stopAll();
        setIsTimerActive(false);
      })();
    }
  }, [isTimerActive, playbackTimerRemaining, setPlaybackTimerRemaining, setIsTimerActive, stopAll]);

  if (currentFrequencies.size === 0) {
    return null;
  }

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSaveToPlaylist = () => {
    const frequencyIds = Array.from(currentFrequencies.values()).map(f => f.frequencyId);
    const name = prompt('Enter playlist name:');
    if (name) {
      saveToPlaylist(name, frequencyIds);
      alert('Saved to playlist!');
    }
  };

  const handleSetTimer = () => {
    const seconds = timerMinutes * 60;
    setPlaybackTimer(seconds);
    setPlaybackTimerRemaining(seconds);
    setIsTimerActive(true);
    setShowTimerModal(false);
  };

  const handleCancelTimer = () => {
    setPlaybackTimer(null);
    setPlaybackTimerRemaining(null);
    setIsTimerActive(false);
  };

  // Get currently playing frequency
  const getCurrentPlayingFrequency = () => {
    if (currentFrequencies.size === 0) return null;
    const activeFreq = Array.from(currentFrequencies.values())[0];
    if (activeFreq && activeFreq.enabled) {
      return getFrequencyById(activeFreq.frequencyId);
    }
    return null;
  };

  // Get any currently playing Gateway frequency (for advanced controls)
  const getCurrentPlayingGatewayFrequency = () => {
    if (currentFrequencies.size === 0) return null;
    // Check all active frequencies to find a Gateway signal
    for (const activeFreq of currentFrequencies.values()) {
      if (activeFreq && activeFreq.enabled) {
        const frequency = getFrequencyById(activeFreq.frequencyId);
        if (frequency?.isGatewaySignal) {
          return frequency;
        }
      }
    }
    return null;
  };

  const currentPlayingFreq = getCurrentPlayingFrequency();
  const currentPlayingGatewayFreq = getCurrentPlayingGatewayFrequency();
  const actuallyPlaying = currentFrequencies.size > 0 && isPlaying;

  const handlePlayPause = async () => {
    if (actuallyPlaying) {
      // Pause - stop all frequencies
      await stopAll();
      setPlaying(false);
    } else {
      // Nothing playing - start with default
      const defaultFreq = getFrequencyById('alpha-10');
      if (defaultFreq) {
        await addFrequency(defaultFreq, 0.7, 0, { source: 'Quick Resume' });
        setPlaying(true);
      }
    }
  };

  return (
    <div className="playback-bar">
      <div className="playback-bar-content">
        <button
          className={`playback-btn ${actuallyPlaying ? 'playing' : ''}`}
          onClick={handlePlayPause}
          aria-label={actuallyPlaying ? 'Pause' : 'Play'}
        >
          {actuallyPlaying ? <Pause size={20} /> : <Play size={20} />}
        </button>

        <div className="playback-info">
          {currentPlayingFreq ? (
            <div className="current-frequency-display">
              <div className="current-freq-name-display">
                <span className="freq-name-text">{currentPlayingFreq.name}</span>
                <span className="freq-value-text">{currentPlayingFreq.frequency} Hz</span>
              </div>
            </div>
          ) : (
            <span className="active-count">
              {currentFrequencies.size} frequency{currentFrequencies.size !== 1 ? 's' : ''} active
            </span>
          )}
          {isTimerActive && playbackTimerRemaining !== null && (
            <div className="timer-display-chunky">
              <Timer size={16} />
              <span className="timer-countdown">{formatTime(playbackTimerRemaining)}</span>
            </div>
          )}
        </div>

        <div className="playback-controls">
          <div className="volume-control">
            <Volume2 size={16} />
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={masterVolume}
              onChange={(e) => setMasterVolume(parseFloat(e.target.value))}
              className="volume-slider"
            />
            <span className="volume-value">{Math.round(masterVolume * 100)}%</span>
          </div>

          {/* Show headphone selector for low frequencies (< 10Hz) */}
          {currentPlayingFreq && currentPlayingFreq.frequency < 10 && (
            <HeadphoneQualitySelector variant="playback" />
          )}

          {/* Show background sound volume controls if any are playing */}
          {Array.from(currentBackgroundSounds.values())
            .filter(sound => sound.enabled)
            .map(activeSound => {
              const sound = getBackgroundSoundById(activeSound.soundId);
              if (!sound) return null;
              
              return (
                <div key={activeSound.id} className="background-sound-control">
                  <Music size={14} />
                  <span className="background-sound-name">{sound.name}</span>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={activeSound.volume}
                    onChange={(e) => setBackgroundSoundVolume(activeSound.id, parseFloat(e.target.value))}
                    className="background-sound-slider"
                    aria-label={`${sound.name} volume`}
                  />
                  <span className="background-sound-volume-value">
                    {Math.round(activeSound.volume * 100)}%
                  </span>
                </div>
              );
            })}

          <button
            className="playback-btn secondary"
            onClick={() => setShowTimerModal(true)}
            aria-label="Set timer"
            title="Set timer"
          >
            <Clock size={16} />
          </button>

          <button
            className="playback-btn secondary"
            onClick={handleSaveToPlaylist}
            aria-label="Save to playlist"
            title="Save to playlist"
          >
            <Save size={16} />
          </button>

          <button
            className={`playback-btn secondary focus-mode-btn ${isFocusMode ? 'active' : ''}`}
            onClick={() => setFocusMode(!isFocusMode)}
            aria-label={isFocusMode ? 'Exit focus mode' : 'Enter focus mode'}
            title={isFocusMode ? 'Exit focus mode' : 'Enter focus mode'}
          >
            <Target size={16} />
          </button>

          <button
            className="playback-btn secondary"
            onClick={async () => await stopAll()}
            aria-label="Stop all"
            title="Stop all"
          >
            <Square size={16} />
          </button>
        </div>

        {showTimerModal && (
          <>
            <div 
              className="timer-modal-overlay"
              onClick={() => setShowTimerModal(false)}
            />
            <div className="timer-modal">
              <div className="timer-modal-content">
                <div className="timer-modal-header">
                  <h3>Set Timer</h3>
                  <button 
                    className="timer-modal-close-x" 
                    onClick={() => setShowTimerModal(false)}
                    aria-label="Close"
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="timer-input-group">
                  <input
                    type="number"
                    min="1"
                    max="120"
                    value={timerMinutes}
                    onChange={(e) => setTimerMinutes(parseInt(e.target.value) || 30)}
                    className="timer-input"
                  />
                  <span>minutes</span>
                </div>
                <div className="timer-modal-actions">
                  <button onClick={handleSetTimer} className="timer-btn primary">
                    Set Timer
                  </button>
                  {isTimerActive && (
                    <button onClick={handleCancelTimer} className="timer-btn secondary">
                      Cancel Timer
                    </button>
                  )}
                  <button onClick={() => setShowTimerModal(false)} className="timer-btn">
                    Close
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Advanced frequency controls - only shows for Gateway frequencies */}
      {currentPlayingGatewayFreq && (
        <div className="playback-bar-advanced">
          <AdvancedFrequencyControls frequencyId={currentPlayingGatewayFreq.id} />
        </div>
      )}
    </div>
  );
};

export default PlaybackBar;
